import { CommandExecutionContext } from "types/commands";
import { StandardToolResult, createSuccessResult, createErrorResult } from "types/standardToolResults";
import { SimpleDocCommand } from "./types";
import { createFileLogger } from '../../../../utils/logger';
import { entityResolver } from '../../../../services/entityResolver';
import { createDocumentReader } from "../../services/doc/DocumentReader";

const log = createFileLogger('DOC_GET_METADATA');

/**
 * Count words in text
 */
function countWords(text: string): number {
  if (!text) return 0;
  return text.trim().split(/\s+/).filter(w => w.length > 0).length;
}

/**
 * Extract heading level from node attributes
 */
function getHeadingLevel(attrs: any): number | null {
  if (!attrs) return null;
  return attrs.level || attrs.heading_level || null;
}

export const docGetMetadataCommand: SimpleDocCommand = {
  id: 'doc_get_metadata',
  
  description: `Retrieve document structure and statistics without reading full content.

**SUPPORTED ENTITIES:**
- Documents (doc_*)
- For Notes/Sheets/Canvas/Slides, use entity_get_metadata instead

Faster than doc_read_v2 when you only need metadata (word counts, node types, structure).

**SELECTORS:**

"all" - Full document stats (totalNodes, totalWords, nodeTypes, headingLevels, structuralElements, distribution)

{"indexRange": {"from": 0, "to": 10}} - Metadata for nodes 0-10 by position
{"range": {"from": "node_abc_123", "to": "node_xyz_789"}} - Metadata between two node IDs
{"nodeIds": ["node_123", "node_456"]} - Metadata for specific nodes
{"query": {"text": "introduction"}} - Metadata for nodes matching search

Range selectors return per-node metadata: id, type, wordCount, charCount, isEmpty, index, headingLevel (for headings).

**OPTIONS:**
- includeAttributes: Include full ProseMirror attrs (default: false)
- includePreview: Include first 100 chars of text (default: false)`,

  logicalName: 'doc_get_metadata',
  version: 'v1',
  appType: 'docs',
  
  formatViewData: (result: any, toolInput: any) => {
    try {
      if (result?.data?.type === 'document_metadata_full') {
        const data = result.data;
        const stats = data.statistics;
        
        // Build summary for full document
        const parts: string[] = [];
        parts.push(`${stats.totalNodes.toLocaleString()} nodes • ${stats.totalWords.toLocaleString()} words`);
        
        if (stats.nodeTypes) {
          const topTypes = Object.entries(stats.nodeTypes)
            .sort(([, a]: any, [, b]: any) => b - a)
            .slice(0, 5)
            .map(([type, count]: [string, any]) => `${count} ${type}${count > 1 ? 's' : ''}`)
            .join(', ');
          if (topTypes) parts.push(topTypes);
        }
        
        return {
          type: 'document_metadata',
          summary: `${data.documentName}: ${parts.join(' • ')}`,
          data: {
            documentName: data.documentName,
            totalNodes: stats.totalNodes,
            totalWords: stats.totalWords,
            nodeTypes: stats.nodeTypes,
            headingLevels: stats.headingLevels
          },
          meta: {
            duration: result.metadata?.executionTime
          }
        };
      }
      
      if (result?.data?.type === 'node_metadata_range') {
        const data = result.data;
        const summary = data.summary;
        
        return {
          type: 'node_metadata',
          summary: `${summary.nodeCount} nodes • ${summary.totalWords} words`,
          data: {
            nodeCount: summary.nodeCount,
            totalWords: summary.totalWords,
            nodeTypes: summary.nodeTypes
          },
          meta: {
            duration: result.metadata?.executionTime
          }
        };
      }
      
      return {
        type: 'tool_summary',
        summary: 'Document metadata retrieved',
        data: result?.data || {}
      };
      
    } catch (error) {
      console.warn('[DOC_GET_METADATA] Error in formatViewData:', error);
      return {
        type: 'tool_summary',
        summary: 'Document metadata retrieved',
        data: {}
      };
    }
  },
  
  schema: {
    parameters: [
      {
        name: 'id',
        type: 'string',
        description: 'Document ID to get metadata from (e.g. "doc_large_pig_coconut" or "file_dark_star_dog")',
        required: true,
        validator: (value: string) => {
          if (!value || value.trim().length === 0) return 'Document ID cannot be empty';
          if (value.startsWith('note_') || value.startsWith('sheet_') || value.startsWith('canvas_') || value.startsWith('slide_') || value.startsWith('presentation_')) {
            return 'doc_get_metadata is for Documents only. Use entity_get_metadata for Notes/Sheets/Canvas/Slides.';
          }
          return null;
        }
      },
      {
        name: 'selector',
        type: 'string',
        description: `What metadata to retrieve (optional, defaults to "all").

Provide "all" as a string, OR a JSON object as a string:

all selector:
  "all" - Full document statistics and structure

indexRange selector:
  '{"indexRange": {"from": 0, "to": 10}}' - Metadata for nodes 0-10 by position
  '{"indexRange": {"start": 0, "count": 10}}' - Metadata for 10 nodes starting at position 0

range selector:
  '{"range": {"from": "node_abc_123", "to": "node_xyz_789"}}' - Metadata for all nodes between two node IDs
  '{"range": {"start": "node_abc_123", "count": 10}}' - Metadata for 10 nodes starting from node ID

nodeIds selector:
  '{"nodeIds": ["node_1234_abc", "node_1234_xyz"]}' - Metadata for specific nodes by ID

query selector:
  '{"query": {"text": "introduction"}}' - Metadata for nodes containing search text`,
        required: false,
        defaultValue: 'all'
      },
      {
        name: 'includeAttributes',
        type: 'boolean',
        description: 'Include full ProseMirror attributes for each node (default: false). When false, only essential metadata is returned for faster performance.',
        required: false,
        defaultValue: false
      },
      {
        name: 'includePreview',
        type: 'boolean',
        description: 'Include text preview (first 100 characters) for each node (default: false). Useful for identifying content without reading the full node.',
        required: false,
        defaultValue: false
      }
    ]
  },
  
  handler: async (params: any, context: CommandExecutionContext): Promise<StandardToolResult> => {
    const startTime = Date.now();
    
    try {
      const { id, selector = 'all', includeAttributes = false, includePreview = false } = params;
      
      console.log('[DOC_GET_METADATA] ========== TOOL EXECUTION START ==========');
      console.log('[DOC_GET_METADATA] Input parameters:', {
        id,
        selector: typeof selector === 'string' ? selector.substring(0, 200) : selector,
        includeAttributes,
        includePreview,
        timestamp: new Date().toISOString()
      });
      
      if (!id) {
        console.error('[DOC_GET_METADATA] Validation failed: Missing document ID');
        return createErrorResult('Missing document ID', 'id parameter is required', 'validation');
      }

      // Resolve entity
      const resolveStartTime = Date.now();
      const resolved = await entityResolver.resolveEntity(id, context);
      const resolveTime = Date.now() - resolveStartTime;
      
      console.log('[DOC_GET_METADATA] Entity resolution:', {
        success: !!resolved?.entity,
        timeTaken: `${resolveTime}ms`,
        entityType: resolved?.entity?.type,
        entityName: resolved?.entity?.name
      });
      
      if (!resolved || !resolved.entity) {
        console.error('[DOC_GET_METADATA] Entity resolution failed:', { id });
        return createErrorResult('Document not found', `Could not find document with ID "${id}"`, 'validation');
      }

      const documentName = resolved.entity.name || 'document';

      // Create document reader (format-agnostic)
      const readerStartTime = Date.now();
      const { reader, error: readerError } = await createDocumentReader(id, context);
      const readerTime = Date.now() - readerStartTime;
      
      if (readerError || !reader) {
        console.error('[DOC_GET_METADATA] Failed to create document reader:', {
          id,
          error: readerError || 'Reader creation failed',
          timeTaken: `${readerTime}ms`
        });
        return createErrorResult('Document not accessible', readerError || `Could not access document "${documentName}"`, 'system');
      }

      console.log('[DOC_GET_METADATA] Document reader created:', {
        id,
        format: reader.format,
        documentName: reader.documentName,
        timeTaken: `${readerTime}ms`
      });

      // Parse selector
      const parseStartTime = Date.now();
      let parsedSelector: any = selector;
      let selectorMode = 'all';
      
      // Handle selector passed as object
      if (typeof selector === 'object' && selector !== null && selector !== 'all') {
        parsedSelector = selector;
      } else if (typeof selector === 'string' && selector !== 'all' && (selector.startsWith('{') || selector.startsWith('['))) {
        try {
          parsedSelector = JSON.parse(selector);
        } catch (e: any) {
          const truncatedSelector = selector.substring(0, 100) + (selector.length > 100 ? '...' : '');
          console.error('[DOC_GET_METADATA] Selector JSON parse failed:', {
            error: e.message,
            selector: truncatedSelector
          });
          return createErrorResult(
            'Invalid selector JSON',
            `Selector appears to be JSON but failed to parse: ${e.message}. Selector: ${truncatedSelector}`,
            'validation'
          );
        }
      }

      // Determine selector mode
      if (typeof parsedSelector === 'object') {
        if (parsedSelector.indexRange) selectorMode = 'indexRange';
        else if (parsedSelector.range) selectorMode = 'range';
        else if (parsedSelector.nodeIds) selectorMode = 'nodeIds';
        else if (parsedSelector.query) selectorMode = 'query';
      }
      
      const parseTime = Date.now() - parseStartTime;
      console.log('[DOC_GET_METADATA] Selector parsed:', { 
        selectorMode, 
        parsedSelector: typeof parsedSelector === 'string' ? parsedSelector : JSON.stringify(parsedSelector).substring(0, 200),
        parseTime: `${parseTime}ms`
      });

      // Handle "all" selector - full document statistics
      if (selectorMode === 'all') {
        console.log('[DOC_GET_METADATA] Processing "all" selector - reading full document');
        const readAllStartTime = Date.now();
        const allContent = reader.readAll();
        const readAllTime = Date.now() - readAllStartTime;
        const allNodes = allContent.nodes;
        
        console.log('[DOC_GET_METADATA] Document read complete:', {
          nodeCount: allNodes.length,
          readTime: `${readAllTime}ms`,
          avgTimePerNode: allNodes.length > 0 ? `${(readAllTime / allNodes.length).toFixed(2)}ms` : '0ms'
        });

        // Handle empty document
        if (allNodes.length === 0) {
          console.log('[DOC_GET_METADATA] Empty document detected, returning default stats');
          return createSuccessResult(
            `Document "${documentName}" is empty (0 nodes)`,
            {
              type: 'document_metadata_full',
              documentId: id,
              documentName,
              selector: 'all',
              statistics: {
                totalNodes: 0,
                totalWords: 0,
                nodeTypes: {},
                headingLevels: {},
                structuralElements: {
                  images: 0,
                  tables: 0,
                  codeBlocks: 0,
                  blockquotes: 0,
                  horizontalRules: 0
                },
                distribution: {
                  averageWordsPerNode: 0,
                  longestNode: null,
                  shortestNode: null
                }
              }
            },
            {
              format: 'json',
              metadata: {
                executionTime: Date.now() - startTime,
                documentName,
                method: 'doc_get_metadata_all'
              } as any
            }
          );
        }

        // Initialize counters
        const processStartTime = Date.now();
        const nodeTypes: Record<string, number> = {};
        const headingLevels: Record<string, number> = {};
        let totalWords = 0;
        let longestNode: any = null;
        let shortestNode: any = null;

        console.log('[DOC_GET_METADATA] Starting node processing:', {
          totalNodesToProcess: allNodes.length
        });

        // Process all nodes
        for (let i = 0; i < allNodes.length; i++) {
          const node = allNodes[i];
          
          // Count node type
          nodeTypes[node.type] = (nodeTypes[node.type] || 0) + 1;
          
          // Count heading levels
          if (node.type === 'heading') {
            const level = getHeadingLevel(node.attrs);
            if (level !== null) {
              headingLevels[level.toString()] = (headingLevels[level.toString()] || 0) + 1;
            }
          }
          
          // Count words
          const nodeWords = countWords(node.text || '');
          totalWords += nodeWords;
          
          // Track longest/shortest
          if (!longestNode || nodeWords > longestNode.wordCount) {
            longestNode = { id: node.id, type: node.type, wordCount: nodeWords };
          }
          if (!shortestNode || nodeWords < shortestNode.wordCount) {
            shortestNode = { id: node.id, type: node.type, wordCount: nodeWords };
          }
        }

        const processTime = Date.now() - processStartTime;
        const avgWordsPerNode = allNodes.length > 0 ? Math.round(totalWords / allNodes.length) : 0;
        
        console.log('[DOC_GET_METADATA] Node processing complete:', {
          processTime: `${processTime}ms`,
          totalWords,
          avgWordsPerNode,
          nodeTypesFound: Object.keys(nodeTypes).length,
          headingLevelsFound: Object.keys(headingLevels).length
        });

        // Count structural elements
        const structuralElements = {
          images: nodeTypes['image'] || 0,
          tables: nodeTypes['table'] || 0,
          codeBlocks: nodeTypes['code_block'] || 0,
          blockquotes: nodeTypes['blockquote'] || 0,
          horizontalRules: nodeTypes['horizontal_rule'] || 0
        };

        const result = {
          type: 'document_metadata_full',
          documentId: id,
          documentName,
          selector: 'all',
          statistics: {
            totalNodes: allNodes.length,
            totalWords,
            nodeTypes,
            headingLevels,
            structuralElements,
            distribution: {
              averageWordsPerNode: avgWordsPerNode,
              longestNode,
              shortestNode
            }
          }
        };

        const totalTime = Date.now() - startTime;
        console.log('[DOC_GET_METADATA] ========== "ALL" MODE SUCCESS ==========');
        console.log('[DOC_GET_METADATA] Performance breakdown:', {
          totalExecutionTime: `${totalTime}ms`,
          entityResolution: `${resolveTime}ms (${((resolveTime / totalTime) * 100).toFixed(1)}%)`,
          readerCreation: `${readerTime}ms (${((readerTime / totalTime) * 100).toFixed(1)}%)`,
          documentRead: `${readAllTime}ms (${((readAllTime / totalTime) * 100).toFixed(1)}%)`,
          nodeProcessing: `${processTime}ms (${((processTime / totalTime) * 100).toFixed(1)}%)`,
          nodeCount: allNodes.length,
          totalWords,
          avgTimePerNode: `${(totalTime / allNodes.length).toFixed(2)}ms`,
          throughput: `${(allNodes.length / (totalTime / 1000)).toFixed(2)} nodes/sec`
        });

        return createSuccessResult(
          `Document "${documentName}": ${allNodes.length} nodes, ${totalWords.toLocaleString()} words`,
          result,
          {
            format: 'json',
            metadata: {
              executionTime: totalTime,
              documentName,
              method: 'doc_get_metadata_all'
            } as any
          }
        );
      }

      // Handle range selectors - per-node metadata
      console.log('[DOC_GET_METADATA] Processing range selector:', { selectorMode });
      const rangeProcessStartTime = Date.now();
      let nodes: any[] = [];
      let rangeInfo: any = {};
      let globalStartIndex: number | undefined = undefined; // For indexRange to map local to global

      if (selectorMode === 'indexRange') {
        const indexRangeParam = parsedSelector.indexRange;
        let fromIndex: number;
        let toIndex: number;
        
        if ('from' in indexRangeParam && 'to' in indexRangeParam) {
          fromIndex = indexRangeParam.from;
          toIndex = indexRangeParam.to;
        } else if ('start' in indexRangeParam && 'count' in indexRangeParam) {
          fromIndex = indexRangeParam.start;
          toIndex = indexRangeParam.start + indexRangeParam.count - 1;
        } else {
          return createErrorResult(
            'Invalid indexRange',
            'indexRange requires either {"from": number, "to": number} or {"start": number, "count": number}',
            'validation'
          );
        }
        
        if (fromIndex < 0 || toIndex < fromIndex) {
          return createErrorResult(
            'Invalid indexRange',
            `Invalid range: from=${fromIndex}, to=${toIndex}. Ensure from >= 0 and to >= from.`,
            'validation'
          );
        }
        
        const allContent = reader.readAll();
        const allNodes = allContent.nodes;
        
        if (toIndex >= allNodes.length) {
          return createErrorResult(
            'Index out of bounds',
            `Index ${toIndex} exceeds document length (${allNodes.length} nodes). Use indices 0-${allNodes.length - 1}.`,
            'validation'
          );
        }
        
        nodes = allNodes.slice(fromIndex, toIndex + 1);
        rangeInfo = { from: fromIndex, to: toIndex, totalDocumentNodes: allNodes.length };
        globalStartIndex = fromIndex; // Store for global index calculation
        
        console.log('[DOC_GET_METADATA] indexRange processed:', {
          fromIndex,
          toIndex,
          nodesExtracted: nodes.length,
          totalDocumentNodes: allNodes.length
        });
        
      } else if (selectorMode === 'range') {
        const rangeParam = parsedSelector.range;
        
        // Check if using start/count format (not supported for range selector)
        if ('start' in rangeParam && 'count' in rangeParam) {
          return createErrorResult(
            'Invalid range selector',
            'range selector with start/count format is not supported. Use {"from": "node_id", "to": "node_id"} instead, or use indexRange for position-based selection with count.',
            'validation'
          );
        }
        
        // Must have from/to format
        if (!('from' in rangeParam) || !('to' in rangeParam)) {
          return createErrorResult(
            'Invalid range selector',
            'range selector requires {"from": "node_id", "to": "node_id"}',
            'validation'
          );
        }
        
        try {
          const rangeResult = reader.readNodeRange(
            rangeParam.from,
            rangeParam.to
          );
          nodes = rangeResult.nodes;
          rangeInfo = { from: rangeParam.from, to: rangeParam.to };
          
          console.log('[DOC_GET_METADATA] range processed:', {
            fromNodeId: rangeParam.from,
            toNodeId: rangeParam.to,
            nodesExtracted: nodes.length
          });
        } catch (error: any) {
          console.error('[DOC_GET_METADATA] range read failed:', {
            error: error.message,
            from: rangeParam.from,
            to: rangeParam.to
          });
          return createErrorResult(
            'Range read failed',
            `Failed to read node range: ${error.message || 'Unknown error'}`,
            'execution'
          );
        }
        
      } else if (selectorMode === 'nodeIds') {
        try {
          const allContent = reader.readAll();
          const requestedIds = parsedSelector.nodeIds;
          nodes = allContent.nodes.filter(n => requestedIds.includes(n.id));
          rangeInfo = { nodeIds: requestedIds, found: nodes.length };
          
          console.log('[DOC_GET_METADATA] nodeIds processed:', {
            requestedCount: requestedIds.length,
            foundCount: nodes.length,
            missingCount: requestedIds.length - nodes.length
          });
        } catch (error: any) {
          console.error('[DOC_GET_METADATA] nodeIds read failed:', error);
          return createErrorResult(
            'NodeIds read failed',
            `Failed to read nodes by ID: ${error.message || 'Unknown error'}`,
            'execution'
          );
        }
        
      } else if (selectorMode === 'query') {
        try {
          const queryText = typeof parsedSelector.query === 'string' 
            ? parsedSelector.query 
            : parsedSelector.query.text;
          const searchResult = reader.search(queryText);
          nodes = searchResult.nodes;
          rangeInfo = { query: queryText, matchCount: nodes.length };
          
          console.log('[DOC_GET_METADATA] query processed:', {
            queryText,
            matchCount: nodes.length
          });
        } catch (error: any) {
          console.error('[DOC_GET_METADATA] query search failed:', {
            error: error.message,
            query: typeof parsedSelector.query === 'string' ? parsedSelector.query : parsedSelector.query.text
          });
          return createErrorResult(
            'Query search failed',
            `Failed to search document: ${error.message || 'Unknown error'}`,
            'execution'
          );
        }
      }

      const rangeProcessTime = Date.now() - rangeProcessStartTime;
      console.log('[DOC_GET_METADATA] Range selector processing complete:', {
        selectorMode,
        nodesFound: nodes.length,
        processingTime: `${rangeProcessTime}ms`
      });

      // Extract metadata from nodes
      const nodeMetadata = nodes.map((node, index) => {
        const text = node.text || '';
        const wordCount = countWords(text);
        const charCount = text.length;
        const isEmpty = text.trim().length === 0;
        
        const metadata: any = {
          id: node.id,
          type: node.type,
          wordCount,
          charCount,     // ✅ NEW: Character count
          isEmpty,       // ✅ NEW: Empty node detection
          index: index  // Local index within selection
        };
        
        // For indexRange, also provide global document index
        if (globalStartIndex !== undefined) {
          metadata.globalIndex = globalStartIndex + index;
        }
        
        // ✅ NEW: Text preview (opt-in)
        if (includePreview && !isEmpty) {
          const previewLength = 100;
          metadata.preview = text.length > previewLength 
            ? text.substring(0, previewLength) + '...' 
            : text;
        }
        
        if (includeAttributes && node.attrs) {
          metadata.attrs = node.attrs;
        } else if (node.type === 'heading') {
          // Always include heading level even when not includeAttributes
          const level = getHeadingLevel(node.attrs);
          if (level !== null) {
            metadata.headingLevel = level;
          }
        }
        
        if (node.children && node.children.length > 0) {
          metadata.hasChildren = true;
        }
        
        return metadata;
      });

      // Calculate summary statistics
      const nodeTypes: Record<string, number> = {};
      let totalWords = 0;
      
      for (const nodeMeta of nodeMetadata) {
        nodeTypes[nodeMeta.type] = (nodeTypes[nodeMeta.type] || 0) + 1;
        totalWords += nodeMeta.wordCount;
      }
      
      const avgWordsPerNode = nodeMetadata.length > 0 ? Math.round(totalWords / nodeMetadata.length) : 0;
      const totalTime = Date.now() - startTime;

      console.log('[DOC_GET_METADATA] ========== RANGE MODE SUCCESS ==========');
      console.log('[DOC_GET_METADATA] Performance breakdown:', {
        totalExecutionTime: `${totalTime}ms`,
        entityResolution: `${resolveTime}ms (${((resolveTime / totalTime) * 100).toFixed(1)}%)`,
        readerCreation: `${readerTime}ms (${((readerTime / totalTime) * 100).toFixed(1)}%)`,
        rangeProcessing: `${rangeProcessTime}ms (${((rangeProcessTime / totalTime) * 100).toFixed(1)}%)`,
        selectorMode,
        nodeCount: nodeMetadata.length,
        totalWords,
        avgWordsPerNode,
        throughput: `${(nodeMetadata.length / (totalTime / 1000)).toFixed(2)} nodes/sec`
      });

      const result = {
        type: 'node_metadata_range',
        documentId: id,
        documentName,
        selector: parsedSelector,
        selectorMode,
        rangeInfo,
        nodes: nodeMetadata,
        summary: {
          nodeCount: nodeMetadata.length,
          totalWords,
          nodeTypes,
          averageWordsPerNode: avgWordsPerNode
        }
      };

      return createSuccessResult(
        `Metadata for ${nodeMetadata.length} nodes: ${totalWords.toLocaleString()} words`,
        result,
        {
          format: 'json',
          metadata: {
            executionTime: totalTime,
            documentName,
            method: `doc_get_metadata_${selectorMode}`
          } as any
        }
      );
      
    } catch (error: any) {
      const totalTime = Date.now() - startTime;
      console.error('[DOC_GET_METADATA] ========== EXECUTION FAILED ==========');
      console.error('[DOC_GET_METADATA] Error details:', {
        error: error.message,
        stack: error.stack,
        executionTime: `${totalTime}ms`,
        params
      });
      return createErrorResult(
        'Metadata retrieval failed',
        `Failed to retrieve metadata: ${error.message || 'Unknown error'}`,
        'execution',
        { 
          data: { 
            errorStack: error instanceof Error ? error.stack : undefined
          },
          metadata: { executionTime: Date.now() - startTime }
        }
      );
    }
  }
};

